import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { FactPicker } from './FactPicker'
import type { FieldSource, SigningFact, SigningField } from './model'

const controlClass =
  'h-8 min-w-0 w-full gap-1 border-transparent bg-transparent text-left shadow-none data-[size=default]:px-2 hover:bg-muted/60 focus-visible:bg-background disabled:opacity-100 disabled:cursor-default disabled:bg-transparent disabled:border-transparent disabled:[&>svg]:invisible [&_[data-slot=select-value]]:block [&_[data-slot=select-value]]:min-w-0 [&_[data-slot=select-value]]:flex-1 [&_[data-slot=select-value]]:truncate'

export function SourceEditor({
  field,
  catalogue,
  source,
  managed,
  disabled = false,
  issue,
  issueId,
  onChange,
}: {
  field: SigningField
  catalogue: SigningFact[]
  source?: FieldSource
  managed: boolean
  disabled?: boolean
  issue?: string
  issueId: string
  onChange: (source: FieldSource) => void
}) {
  // Keep the source row empty when it belongs to the organization.
  if (managed && disabled) return <div className="h-8" />

  const facts = catalogue.filter((fact) =>
    (fact.field_types ?? [fact.type]).includes(field.type)
  )
  const invalid = issue
    ? ({
        'aria-invalid': true,
        'aria-describedby': issueId,
      } as const)
    : {}
  return (
    <div className="grid min-w-0 grid-cols-[88px_minmax(0,1fr)] items-center gap-1">
      <Select
        disabled={disabled}
        value={source?.kind ?? 'literal'}
        onValueChange={(kind) =>
          onChange(
            kind === 'literal'
              ? {
                  kind: 'literal',
                  value: field.type === 'checkbox' ? false : '',
                }
              : { kind: 'fact', binding: '' }
          )
        }>
        <SelectTrigger
          className={cn(controlClass, 'px-2 text-xs text-muted-foreground')}
          aria-label={`${field.label} source`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="literal">Entered</SelectItem>
          <SelectItem value="fact" disabled={!facts.length}>
            Fact
          </SelectItem>
        </SelectContent>
      </Select>
      {source?.kind === 'fact' ? (
        <FactPicker
          facts={facts}
          value={source.binding}
          label={`${field.label} fact`}
          disabled={disabled}
          className={cn(
            controlClass,
            'justify-between px-2',
            issue &&
              'border border-destructive/60 disabled:border-destructive/60'
          )}
          invalid={invalid}
          onChange={(selected) =>
            onChange({
              kind: 'fact',
              binding: selected.binding,
              ...(managed ? { scope: selected.scope } : {}),
            })
          }
        />
      ) : field.type === 'checkbox' ? (
        <div className="flex h-8 items-center px-2">
          <Checkbox
            {...invalid}
            disabled={disabled}
            className="disabled:opacity-100 disabled:cursor-default"
            aria-label={`${field.label} value`}
            checked={source?.value === true}
            onCheckedChange={(checked) =>
              onChange({ kind: 'literal', value: checked === true })
            }
          />
        </div>
      ) : (
        <Input
          {...invalid}
          disabled={disabled}
          className={cn(controlClass, 'px-2', issue && 'border-destructive/60')}
          aria-label={`${field.label} value`}
          value={String(source?.value ?? '')}
          placeholder={disabled ? undefined : 'Enter value'}
          onChange={(event) =>
            onChange({ kind: 'literal', value: event.target.value })
          }
        />
      )}
    </div>
  )
}
